import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useIsLandscape, useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useExpauseLocalizations } from '@/presentation/locale/expauseLocalizations';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { RoadmapItemEntity } from '@/domain/entities/roadmap/roadmapItemEntity';
import { RoadmapItemActionsRow } from './RoadmapItemActionsRow';
import { RoadmapItemAuthorRow } from './RoadmapItemAuthorRow';
import { RoadmapItemSupportBlock } from './RoadmapItemSupportBlock';

/**
 * A single roadmap (Ideas) card.
 *
 * Ports `roadmap_list_item.dart:33-285`: centered title + optional
 * description, a tappable author row (avatar + "Idea from {name}"), a
 * "Status: {label}" row, the like/dislike/comment counter row, and the
 * "{usd} USD raised" + "Show support" donation block.
 *
 * Real actions: tapping the author avatar/name (`onUserTap(item.userId)` →
 * user-details), tapping the comments group (`onComments(item)` → the roadmap
 * comments sheet for this item, Flutter's `_commentsTapped`,
 * roadmap_screen.dart:353-362), and like / dislike (the page's owner
 * short-circuit opens the likes sheet; the non-owner path toggles a reaction).
 * Show-support remains deferred — the page routes it to the canonical
 * not-implemented sheet. Tapping the card body itself does nothing, matching
 * Flutter's `_itemTapped` no-op
 * (roadmap_screen.dart:162-164, whose only statement is commented out). The
 * viewer's like state (`item.likeEntity`) tints the like/dislike icons.
 */
interface RoadmapListItemProps {
  item: RoadmapItemEntity;
  onUserTap: (userId: string) => void;
  onLike: () => void;
  onDislike: () => void;
  onComments: (item: RoadmapItemEntity) => void;
  onShowSupport: () => void;
}

export const RoadmapListItem: React.FC<RoadmapListItemProps> = ({
  item,
  onUserTap,
  onLike,
  onDislike,
  onComments,
  onShowSupport,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const isLandscape = useIsLandscape();
  const { l10n } = useLocale();
  const { expauseLocalizations } = useExpauseLocalizations();

  const liked = item.likeEntity?.liked ?? false;
  const disliked = item.likeEntity?.disliked ?? false;

  const statsTextStyle: React.CSSProperties = {
    color: theme.text.primary,
    fontSize: responsive.font.base,
  };

  const insetDivider = (
    <div
      style={{
        padding: `${responsive.spacing.base}px ${responsive.spacing['2xl']}px`,
      }}
    >
      <div
        style={{
          width: '100%',
          height: responsive.border.thin,
          backgroundColor: theme.separator,
        }}
      />
    </div>
  );

  return (
    <div
      {...qaAttr({ id: `roadmap-item-${item.id}` })}
      style={{
        margin: `${responsive.spacing.xxs}px 0`,
        width: '100%',
        boxSizing: 'border-box',
        backgroundColor: theme.surface,
        padding: responsive.spacing.base,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...(isLandscape ? { borderRadius: responsive.borderRadius.base } : {}),
      }}
    >
      <h3
        style={{
          margin: 0,
          textAlign: 'center',
          color: theme.text.primary,
          fontSize: responsive.font.lg,
          fontWeight: responsive.fontWeight.semibold,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {item.title}
      </h3>

      {item.description && (
        <p
          style={{
            margin: 0,
            marginTop: responsive.spacing.base,
            textAlign: 'center',
            color: theme.text.secondary,
            fontSize: responsive.font.base,
            lineHeight: 1.5,
          }}
        >
          {item.description}
        </p>
      )}

      <RoadmapItemAuthorRow
        itemId={item.id}
        userId={item.userId}
        userName={item.userName}
        onUserTap={onUserTap}
      />

      {insetDivider}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: responsive.gap.xs,
        }}
      >
        <AssetIcon
          path="/assets/images/status.png"
          width={responsive.icon.small}
          height={responsive.icon.small}
          color={theme.white}
        />
        <span style={statsTextStyle}>
          {l10n.roadmap.statusLabel.replace(
            '{value}',
            expauseLocalizations.getRoadmapItemStatusLabel(item.status),
          )}
        </span>
      </div>

      {insetDivider}

      <RoadmapItemActionsRow
        itemId={item.id}
        likes={item.likes}
        dislikes={item.dislikes}
        comments={item.comments}
        liked={liked}
        disliked={disliked}
        onLike={onLike}
        onDislike={onDislike}
        onComments={() => onComments(item)}
      />

      <RoadmapItemSupportBlock
        itemId={item.id}
        usd={item.usd}
        onShowSupport={onShowSupport}
      />
    </div>
  );
};
